import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from '@mui/material';
import {
  PictureAsPdf,
  Visibility,
  Download,
  Close,
  CheckCircle,
  Description,
  CreditCard,
} from '@mui/icons-material';
import PdfViewer from 'components/common/PdfViewer/PdfViewer';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';

interface DocumentData {
  id: string;
  object: string;
  for_id: string;
  for_type: string;
  type: string;
  mime: string;
  file_name: string;
  path_to_file: string;
  download_url: string;
  expires_in: null;
  meta: null;
  created_at: string;
  updated_at: string;
}

interface AllianzConfirmationProps {
  documents: DocumentData[];
  isLoadingDocuments: boolean;
  pollingCompleted: boolean;
  operationNumber?: string | number | null;
  proposalNumber?: string | number | null;
  policyNumber?: string | number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AllianzConfirmation: React.FC<AllianzConfirmationProps> = ({
  documents,
  isLoadingDocuments,
  pollingCompleted,
  operationNumber,
  proposalNumber,
  policyNumber,
}) => {
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const selectedProducts = useSelector((state: RootState) => state.client.selectedProducts);

  // Categorize documents
  const apoliceDocuments = documents.filter(doc => 
    !doc.file_name.toLowerCase().includes('proposal') && 
    !doc.file_name.toLowerCase().includes('proposta') &&
    !doc.file_name.toLowerCase().includes('carteirinha')
  );
  
  const propostaDocuments = documents.filter(doc => 
    doc.file_name.toLowerCase().includes('proposal') || 
    doc.file_name.toLowerCase().includes('proposta')
  );
  
  const carteirinhaDocuments = documents.filter(doc => 
    doc.file_name.toLowerCase().includes('carteirinha')
  );

  // Available tabs based on document availability
  const availableTabs = [
    { label: 'Apólice', documents: apoliceDocuments, icon: <CheckCircle /> },
    { label: 'Proposta', documents: propostaDocuments, icon: <Description /> },
    { label: 'Carteirinha', documents: carteirinhaDocuments, icon: <CreditCard /> },
  ].filter(tab => tab.documents.length > 0);

  // Set default active tab to first available tab
  useEffect(() => {
    if (availableTabs.length > 0) {
      setActiveTab(0);
    }
  }, [documents]);

  const handleShowPdf = (document: DocumentData) => {
    setSelectedDocument(document);
    setPdfDialogOpen(true);
  };

  const handleClosePdf = () => {
    setPdfDialogOpen(false);
    setSelectedDocument(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderDocumentList = (docs: DocumentData[]) => {
    if (docs.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Nenhum documento disponível nesta categoria
        </Typography>
      );
    }

    return (
      <List sx={{ p: 0 }}>
        {docs.map((document, index) => (
          <React.Fragment key={document.id}>
            <ListItem
              sx={{
                p: 2,
                borderRadius: 1,
                mb: 1,
                bgcolor: 'grey.50',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              <ListItemIcon>
                <PictureAsPdf color="error" />
              </ListItemIcon>
              
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight={500}>
                    {document.file_name}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Criado em: {new Date(document.created_at).toLocaleDateString('pt-BR')}
                  </Typography>
                }
              />
              
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => handleShowPdf(document)}
                  sx={{ minWidth: 100 }}
                >
                  Visualizar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  component="a"
                  href={document.download_url}
                  download={document.file_name}
                  sx={{ minWidth: 100 }}
                >
                  Baixar
                </Button>
              </Box>
            </ListItem>
            {index < docs.length - 1 && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
      </List>
    );
  };


  // Determine title based on product type
  const getConfirmationTitle = () => {
    if (selectedProducts.includes('HOMEOWNERS-ALLIANZ')) {
      return 'Obrigado por escolher o seguro RESIDENCIAL da Allianz';
    }
    return 'Obrigado por escolher o seguro Automóvel Allianz!';
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          {getConfirmationTitle()}
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, mt: 0.75, flexWrap: 'wrap' }}>
          {operationNumber && (
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Operação:
              </Box>{' '}
              {operationNumber}
            </Typography>
          )}
          {proposalNumber && (
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Proposta:
              </Box>{' '}
              {proposalNumber}
            </Typography>
          )}
          {policyNumber && (
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Apólice:
              </Box>{' '}
              {policyNumber}
            </Typography>
          )}
        </Box>
      </Box>
      
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Você pode baixar os documentos* relacionados ao seguro nos links a seguir:
      </Typography>

      {isLoadingDocuments ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Carregando documentos...
          </Typography>
        </Box>
      ) : availableTabs.length > 0 ? (
        <Card sx={{ mt: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  minHeight: 60,
                  fontSize: '1rem',
                  fontWeight: 500,
                },
              }}
            >
              {availableTabs.map((tab, index) => (
                <Tab 
                  key={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  sx={{ 
                    color: 'primary.main',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 'bold',
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>
          
          {availableTabs.map((tab, index) => (
            <TabPanel key={tab.label} value={activeTab} index={index}>
              {renderDocumentList(tab.documents)}
            </TabPanel>
          ))}
        </Card>
      ) : pollingCompleted ? (
        <Card sx={{ mt: 3, p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum documento disponível no momento
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Não há documentos de apólice disponíveis no momento. Os documentos podem levar algum tempo para serem gerados. Por favor, verifique mais tarde ou entre em contato com o suporte se precisar de assistência.
          </Typography>
        </Card>
      ) : (
        <Card sx={{ mt: 3, p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Carregando documentos...
          </Typography>
        </Card>
      )}

      {/* Footer Note */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          *Os documentos também serão enviados para o seu e-mail cadastrado em até 2 dias.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Em caso de dúvidas, procure seu corretor.
        </Typography>
      </Box>

      {/* PDF Viewer Dialog */}
      <Dialog
        open={pdfDialogOpen}
        onClose={handleClosePdf}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            maxHeight: 'none',
          },
        }}
      >
        <DialogActions sx={{ p: 1, justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {selectedDocument?.file_name}
          </Typography>
          <IconButton onClick={handleClosePdf}>
            <Close />
          </IconButton>
        </DialogActions>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {selectedDocument && (
            <Box sx={{ flex: 1, height: '100%' }}>
              <PdfViewer
                documentData={selectedDocument}
                onBack={handleClosePdf}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AllianzConfirmation;
